import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from './ProductCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductGrid = ({ 
  products, 
  viewMode, 
  sortBy, 
  onAddToCart, 
  onAddToWishlist, 
  onCompare,
  wishlistItems,
  comparisonItems,
  isLoading 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const productsPerPage = 12;

  useEffect(() => {
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    setDisplayedProducts(products?.slice(0, endIndex));
  }, [products, currentPage]);

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const hasMore = displayedProducts?.length < products?.length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading Skeleton */}
        <div className={`grid gap-6 ${
          viewMode === 'grid' ?'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' :'grid-cols-1'
        }`}>
          {Array.from({ length: 8 })?.map((_, index) => (
            <div key={index} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse">
              <div className="h-48 bg-muted/50"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-muted/50 rounded w-3/4"></div>
                <div className="h-3 bg-muted/50 rounded w-1/2"></div>
                <div className="h-6 bg-muted/50 rounded w-1/3"></div>
                <div className="h-10 bg-muted/50 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (products?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-24 h-24 bg-muted/20 rounded-full flex items-center justify-center mb-6">
          <Icon name="Search" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">No Products Found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          We couldn't find any products matching your current filters. Try adjusting your search criteria or browse our categories.
        </p>
        <Button variant="outline" iconName="RotateCcw" iconPosition="left">
          Reset Filters
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {displayedProducts?.length} of {products?.length} products
        </div>
        <div className="text-sm text-muted-foreground">
          Sorted by: <span className="text-foreground font-medium">{sortBy}</span>
        </div>
      </div>
      {/* Product Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className={`grid gap-6 ${
          viewMode === 'grid' ?'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' :'grid-cols-1 max-w-4xl mx-auto'
        }`}
      >
        <AnimatePresence>
          {displayedProducts?.map((product) => (
            <motion.div
              key={product?.id}
              variants={itemVariants}
              layout
              className={viewMode === 'list' ? 'w-full' : ''}
            >
              <ProductCard
                product={product}
                onAddToCart={onAddToCart}
                onAddToWishlist={onAddToWishlist}
                onCompare={onCompare}
                isInWishlist={wishlistItems?.includes(product?.id)}
                isInComparison={comparisonItems?.includes(product?.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {/* Load More / Pagination */}
      {hasMore && (
        <div className="flex justify-center pt-8">
          <Button
            variant="outline"
            onClick={loadMore}
            iconName="ChevronDown"
            iconPosition="right"
            className="px-8"
          >
            Load More Products
          </Button>
        </div>
      )}
      {/* Back to Top */}
      {displayedProducts?.length > 12 && (
        <div className="flex justify-center pt-4">
          <Button
            variant="ghost"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            iconName="ChevronUp"
            iconPosition="left"
            size="sm"
          >
            Back to Top
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;